import { postApiEmbeddingAddTextToData, putApiEmbeddingUpdateDataPointText, useDeleteApiEmbeddingDeleteDataPoint, useGetApiEmbeddingCollectionGet, useGetApiEmbeddingListTextData, useGetApiWorkspaceGet } from "@/API"
import { ActionIcon, Box, Breadcrumbs, Button, Center, Divider, Group, LoadingOverlay, Pagination, Stack, Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { IconDatabaseOff, IconEdit, IconInfoCircle, IconTrash } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import { Link, useParams } from "@tanstack/react-router"
import { DataTable } from "mantine-datatable"
import { useState } from "react"
import { EmbeddingAddExcelButton } from "../EmbeddingCollection/EmbeddingAddExcelButton"
import { EmbeddingReplaceDataButton } from "./EmbeddingReplaceDataButton"
import { PageTitle } from "@/Components/PageTitle"
import { EmbeddingTextDataEditButton } from "./EmbeddingTextDataEditButton"
import { AppBreadcrumbs } from "@/Components/AppBreadcrumbs"

export function EmbeddingIndex() {
    const { workspaceId, embeddingCollectionId, dataId } = useParams({ from: "/workspace/$workspaceId/embeddingCollection/$embeddingCollectionId/$dataId/" })
    const queryClient = useQueryClient()
    const workspace = useGetApiWorkspaceGet({ id: workspaceId })

    const PAGE_SIZE = 10
    const [activePage, setPage] = useState(1);
    const collection = useGetApiEmbeddingCollectionGet({ workspaceId: workspaceId, id: embeddingCollectionId })
    const dataDescription = collection.data?.data.embeddings?.filter(x => x.dataId == dataId)?.at(0)?.description ?? dataId
    const list = useGetApiEmbeddingListTextData({ workspaceId: workspaceId, collectionId: embeddingCollectionId, dataId: dataId, skip: (activePage - 1) * PAGE_SIZE, limit: PAGE_SIZE })
    const delete_Mutation = useDeleteApiEmbeddingDeleteDataPoint()
    return <Stack pos="relative" gap={5}>
        <LoadingOverlay visible={list.isFetching} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
        <AppBreadcrumbs
            items={[
                {
                    label: '工作空間列表',
                    linkProps: { to: '/workspace' },
                },
                {
                    label: workspace.data?.data.name ?? '',
                    linkProps: { to: '/workspace/$workspaceId', params: { workspaceId } },
                },
                {
                    label: '資料集合列表',
                    linkProps: { to: '/workspace/$workspaceId/embeddingCollection', params: { workspaceId } },
                },
                {
                    label: collection.data?.data.name,
                    linkProps: { to: '/workspace/$workspaceId/embeddingCollection/$embeddingCollectionId', params: { workspaceId, embeddingCollectionId } },
                },
                {
                    label: dataDescription,
                    active: true
                }
            ]}
        />
        <PageTitle text={"資料"} mb={10} />

        <Box>
            <EmbeddingTextDataEditButton title="新增內容" onChange={async v => {
                try {
                    await postApiEmbeddingAddTextToData(v, {
                        workspaceId: workspaceId,
                        collectionId: embeddingCollectionId,
                        dataId: dataId
                    }, {
                        headers: { "Content-Type": "application/json" }
                    })
                    queryClient.invalidateQueries({ queryKey: list.queryKey })
                    notifications.show({ message: "新增成功" })
                } catch (e) {
                    notifications.show({ message: "新增失敗", color: "red" })
                }
            }}>
                <Button size="sm">
                    新增內容
                </Button>
            </EmbeddingTextDataEditButton>
        </Box>
        {
            embeddingCollectionId && dataId && <>
                <Group>
                    <EmbeddingReplaceDataButton workspaceId={workspaceId} embeddingCollectionId={embeddingCollectionId} dataId={dataId}>
                        <>
                            上傳Excel並覆蓋此集合
                        </>
                    </EmbeddingReplaceDataButton>
                </Group>
            </>
        }

        <Pagination value={activePage} onChange={setPage} total={Math.ceil((list.data?.data.total ?? 0) / PAGE_SIZE)} />
        <DataTable
            withTableBorder
            minHeight={list.data?.data.results && list.data?.data.results?.length > 0 ? 0 : 150}
            noRecordsText="沒有資料"
            columns={[
                { accessor: 'pointId', title: "PointId" },
                { accessor: 'text', title: "內容" },
                {
                    accessor: 'template', title: "Template", render: d => d.template ? d.template : <Center>
                        <IconDatabaseOff color="gray" stroke={2} />
                    </Center>
                },
                {
                    accessor: 'actions',
                    title: <Box mr={6}>操作</Box>,
                    textAlign: 'right',
                    render: (data) => (
                        <Group gap={4} justify="right" wrap="nowrap">
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                onClick={() => {
                                    modals.open({
                                        withCloseButton: false,
                                        children: <Text style={{ whiteSpace: "pre-wrap" }}>
                                            {data.text}
                                        </Text>
                                    })
                                }}>
                                <IconInfoCircle size={16} />
                            </ActionIcon>
                            <EmbeddingTextDataEditButton defaultData={data} onChange={async (v) => {
                                try {
                                    if (data.pointId) {
                                        await putApiEmbeddingUpdateDataPointText(v, {
                                            workspaceId: workspaceId,
                                            collectionId: embeddingCollectionId,
                                            dataId: dataId,
                                            pointId: data.pointId
                                        }, {
                                            headers: { "Content-Type": "application/json" }
                                        })
                                        queryClient.invalidateQueries({ queryKey: list.queryKey })
                                        notifications.show({ message: "修改成功" })
                                    }
                                } catch (e) {
                                    notifications.show({ message: "修改失敗", color: "red" })
                                }
                            }}>
                                <ActionIcon size="sm" variant="subtle">
                                    <IconEdit size={16} />
                                </ActionIcon>
                            </EmbeddingTextDataEditButton>
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="red"
                                onClick={() => {
                                    modals.openContextModal({
                                        modal: "ConfirmCountDown",
                                        title: '刪除',
                                        innerProps: {
                                            countDownSecond: 1,
                                            children: (
                                                <Text size="sm">
                                                    是否要刪除"{data.pointId}"?
                                                </Text>
                                            ),
                                            labels: {
                                                confirm: '刪除', cancel: '取消'
                                            },
                                            confirmProps: { color: 'red' },
                                            onConfirm: async () => {
                                                try {
                                                    if (data.pointId) {
                                                        await delete_Mutation.mutateAsync({
                                                            params: {
                                                                workspaceId: workspaceId,
                                                                collectionId: embeddingCollectionId,
                                                                dataId: dataId,
                                                                pointId: data.pointId
                                                            }
                                                        })
                                                        queryClient.invalidateQueries({ queryKey: list.queryKey })
                                                        notifications.show({ message: "刪除成功" })
                                                    }
                                                } catch (e) {
                                                    notifications.show({ message: "刪除失敗", color: "red" })
                                                }
                                            },
                                        },

                                    });
                                }}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Group>
                    ),
                },
            ]}
            onRowDoubleClick={({ record }) => {
                modals.open({
                    withCloseButton: false,
                    size: "80%",
                    children: <Group align="start">
                        <Text flex={1} style={{ whiteSpace: "pre-wrap" }}>
                            {record.text}
                        </Text>
                        {record.template && <>
                            <Divider size="md" orientation="vertical" />
                            <Text flex={1} style={{ whiteSpace: "pre-wrap" }}>
                                {record.template}
                            </Text>
                        </>}
                    </Group>
                })
            }}
            records={list.data?.data.results?.map((x, idx) => {
                return {
                    id: x.pointId,
                    ...x
                }
            }) ?? []}
        />
    </Stack>
}